import React, { useState } from 'react';

import {
  Clock, AlertTriangle,
  Download, ExternalLink,
  ChevronDown, ChevronRight, CreditCard,
  IndianRupee,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import PermissionGate from '../../../components/common/PermissionGate';
import { formatIndianAmount } from '../../../utils/amountFormatter';

const PartyFinancials = () => {
  const [dateRange, setDateRange] = useState('90d');
  const [agingBucket, setAgingBucket] = useState('all');
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Sorting state for top parties
  const [sortColumn, setSortColumn] = useState('totalOutstanding');
  const [sortDirection, setSortDirection] = useState('desc');

  // Mock data - would come from Marg ERP API
  const kpiData = {
    totalOutstanding: { value: 45750000, change: 8.5, trend: 'up' },
    collections: { value: 12850000, change: -3.2, trend: 'down' },
    overduePercentage: { value: 23.8, change: 1.5, trend: 'up' },
    avgCollectionPeriod: { value: 42.5, change: -2.1, trend: 'down' }
  };

  const agingBuckets = [
    { bucket: '0-30 days', amount: 18500000, percentage: 40.4, count: 245, color: 'bg-green-500' },
    { bucket: '31-60 days', amount: 12200000, percentage: 26.7, count: 156, color: 'bg-yellow-500' },
    { bucket: '61-90 days', amount: 8750000, percentage: 19.1, count: 98, color: 'bg-orange-500' },
    { bucket: '91-120 days', amount: 4200000, percentage: 9.2, count: 67, color: 'bg-red-500' },
    { bucket: '120+ days', amount: 2100000, percentage: 4.6, count: 34, color: 'bg-red-700' }
  ];

  const collectionsData = [
    { period: 'Week 1', target: 3200000, collected: 2850000, percentage: 89.1 },
    { period: 'Week 2', target: 3200000, collected: 3150000, percentage: 98.4 },
    { period: 'Week 3', target: 3200000, collected: 2950000, percentage: 92.2 },
    { period: 'Week 4', target: 3200000, collected: 3900000, percentage: 121.9 }
  ];

  const topParties = [
    {
      id: 'PTY001',
      name: 'Reliance Industries Ltd',
      type: 'Corporate',
      region: 'West',
      branch: 'Mumbai Central',
      rm: 'Rajesh Kumar',
      totalOutstanding: 8750000,
      overdue: 2100000,
      overduePercentage: 24.0,
      avgCollectionDays: 45,
      creditLimit: 15000000,
      lastPayment: '2024-01-15',
      lastPaymentAmount: 1250000,
      riskScore: 'Medium',
      agingBreakdown: {
        '0-30': 4200000,
        '31-60': 2450000,
        '61-90': 1500000,
        '91-120': 600000,
        '120+': 0
      },
      recentInvoices: [
        { id: 'INV-2024-001', date: '2024-01-18', amount: 850000, status: 'Pending', dueDate: '2024-02-17' },
        { id: 'INV-2024-002', date: '2024-01-10', amount: 1200000, status: 'Overdue', dueDate: '2024-02-09' },
        { id: 'INV-2023-456', date: '2023-12-28', amount: 750000, status: 'Paid', dueDate: '2024-01-27' }
      ],
      paymentHistory: [
        { date: '2024-01-15', amount: 1250000, method: 'Bank Transfer', reference: 'TXN123456' },
        { date: '2024-01-08', amount: 950000, method: 'Cheque', reference: 'CHQ789012' },
        { date: '2023-12-30', amount: 1800000, method: 'NEFT', reference: 'NEFT345678' }
      ]
    },
    {
      id: 'PTY002',
      name: 'Tata Consultancy Services',
      type: 'Corporate',
      region: 'West',
      branch: 'Pune Tech',
      rm: 'Priya Sharma',
      totalOutstanding: 6250000,
      overdue: 850000,
      overduePercentage: 13.6,
      avgCollectionDays: 32,
      creditLimit: 10000000,
      lastPayment: '2024-01-18',
      lastPaymentAmount: 2100000,
      riskScore: 'Low',
      agingBreakdown: {
        '0-30': 3800000,
        '31-60': 1600000,
        '61-90': 850000,
        '91-120': 0,
        '120+': 0
      },
      recentInvoices: [
        { id: 'INV-2024-003', date: '2024-01-20', amount: 1150000, status: 'Pending', dueDate: '2024-02-19' },
        { id: 'INV-2024-004', date: '2024-01-12', amount: 950000, status: 'Pending', dueDate: '2024-02-11' }
      ],
      paymentHistory: [
        { date: '2024-01-18', amount: 2100000, method: 'RTGS', reference: 'RTGS567890' },
        { date: '2024-01-05', amount: 1650000, method: 'Bank Transfer', reference: 'TXN234567' }
      ]
    },
    {
      id: 'PTY003',
      name: 'Infosys Limited',
      type: 'Corporate',
      region: 'South',
      branch: 'Bangalore Tech',
      rm: 'Amit Patel',
      totalOutstanding: 5850000,
      overdue: 1950000,
      overduePercentage: 33.3,
      avgCollectionDays: 58,
      creditLimit: 8000000,
      lastPayment: '2024-01-10',
      lastPaymentAmount: 750000,
      riskScore: 'High',
      agingBreakdown: {
        '0-30': 2100000,
        '31-60': 1800000,
        '61-90': 1200000,
        '91-120': 750000,
        '120+': 0
      },
      recentInvoices: [
        { id: 'INV-2024-005', date: '2024-01-16', amount: 1350000, status: 'Pending', dueDate: '2024-02-15' },
        { id: 'INV-2024-006', date: '2024-01-08', amount: 850000, status: 'Overdue', dueDate: '2024-02-07' }
      ],
      paymentHistory: [
        { date: '2024-01-10', amount: 750000, method: 'Cheque', reference: 'CHQ890123' },
        { date: '2023-12-28', amount: 1200000, method: 'NEFT', reference: 'NEFT456789' }
      ]
    },
    {
      id: 'PTY004',
      name: 'Wipro Technologies',
      type: 'Corporate',
      region: 'South',
      branch: 'Chennai Port',
      rm: 'Sneha Reddy',
      totalOutstanding: 4200000,
      overdue: 650000,
      overduePercentage: 15.5,
      avgCollectionDays: 38,
      creditLimit: 7000000,
      lastPayment: '2024-01-17',
      lastPaymentAmount: 1850000,
      riskScore: 'Low',
      agingBreakdown: {
        '0-30': 2450000,
        '31-60': 1100000,
        '61-90': 650000,
        '91-120': 0,
        '120+': 0
      },
      recentInvoices: [
        { id: 'INV-2024-007', date: '2024-01-19', amount: 950000, status: 'Pending', dueDate: '2024-02-18' },
        { id: 'INV-2024-008', date: '2024-01-14', amount: 750000, status: 'Pending', dueDate: '2024-02-13' }
      ],
      paymentHistory: [
        { date: '2024-01-17', amount: 1850000, method: 'RTGS', reference: 'RTGS678901' },
        { date: '2024-01-03', amount: 1100000, method: 'Bank Transfer', reference: 'TXN345678' }
      ]
    },
    {
      id: 'PTY005',
      name: 'HCL Technologies',
      type: 'Corporate',
      region: 'North',
      branch: 'Delhi NCR',
      rm: 'Vikram Singh',
      totalOutstanding: 3950000,
      overdue: 1200000,
      overduePercentage: 30.4,
      avgCollectionDays: 52,
      creditLimit: 6000000,
      lastPayment: '2024-01-12',
      lastPaymentAmount: 950000,
      riskScore: 'Medium',
      agingBreakdown: {
        '0-30': 1850000,
        '31-60': 900000,
        '61-90': 750000,
        '91-120': 450000,
        '120+': 0
      },
      recentInvoices: [
        { id: 'INV-2024-009', date: '2024-01-21', amount: 1100000, status: 'Pending', dueDate: '2024-02-20' },
        { id: 'INV-2024-010', date: '2024-01-13', amount: 650000, status: 'Overdue', dueDate: '2024-02-12' }
      ],
      paymentHistory: [
        { date: '2024-01-12', amount: 950000, method: 'Cheque', reference: 'CHQ901234' },
        { date: '2023-12-29', amount: 1350000, method: 'NEFT', reference: 'NEFT567890' }
      ]
    }
  ];

  const regionPerformance = [
    {
      region: 'West',
      totalOutstanding: 18500000,
      collections: 5200000,
      overduePercentage: 18.5,
      avgCollectionDays: 38,
      parties: 125,
      topParty: 'Reliance Industries Ltd'
    },
    {
      region: 'South',
      totalOutstanding: 15200000,
      collections: 4100000,
      overduePercentage: 24.2,
      avgCollectionDays: 48,
      parties: 98,
      topParty: 'Infosys Limited'
    },
    {
      region: 'North',
      totalOutstanding: 8750000,
      collections: 2350000,
      overduePercentage: 28.5,
      avgCollectionDays: 45,
      parties: 67,
      topParty: 'HCL Technologies'
    },
    {
      region: 'East',
      totalOutstanding: 3300000,
      collections: 1200000,
      overduePercentage: 32.1,
      avgCollectionDays: 55,
      parties: 34,
      topParty: 'Tech Mahindra Ltd'
    }
  ];

  const formatCurrency = (amount) => {
    return `₹${formatIndianAmount(amount)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'Low':
        return 'text-green-600 bg-green-50';
      case 'Medium':
        return 'text-yellow-600 bg-yellow-50';
      case 'High':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid':
        return 'text-green-600 bg-green-50';
      case 'Pending':
        return 'text-yellow-600 bg-yellow-50';
      case 'Overdue':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getOverdueColor = (percentage) => {
    if (percentage >= 30) return 'text-red-600';
    if (percentage >= 20) return 'text-yellow-600';
    return 'text-green-600';
  };

  // Handle drill down - removed popup notifications
  const handleDrillDown = (type, filters = {}) => {
    // In real app: navigate to appropriate view
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sorted top parties
  const getSortedParties = () => {
    if (!sortColumn || !topParties || topParties.length === 0) return topParties;

    return [...topParties].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === 'name') {
        aValue = (a.name || '').toLowerCase();
        bValue = (b.name || '').toLowerCase();
      } else if (sortColumn === 'totalOutstanding') {
        aValue = Number(a.totalOutstanding) || 0;
        bValue = Number(b.totalOutstanding) || 0;
      } else if (sortColumn === 'overdue') {
        aValue = Number(a.overdue) || 0;
        bValue = Number(b.overdue) || 0;
      } else if (sortColumn === 'avgCollectionDays') {
        aValue = Number(a.avgCollectionDays) || 0;
        bValue = Number(b.avgCollectionDays) || 0;
      } else if (sortColumn === 'riskScore') {
        const riskOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
        aValue = riskOrder[a.riskScore] || 0;
        bValue = riskOrder[b.riskScore] || 0;
      } else if (sortColumn === 'lastPayment') {
        aValue = new Date(a.lastPayment || 0).getTime();
        bValue = new Date(b.lastPayment || 0).getTime();
      } else {
        aValue = a[sortColumn] || '';
        bValue = b[sortColumn] || '';
      }

      const order = sortDirection === 'asc' ? 1 : -1;
      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
      return 0;
    });
  };

  // FIXED: Enhanced export functionality
  const handleExport = (format, data) => {
    let csvData = [];
    let filename = '';

    switch (format) {
      case 'excel':
      case 'all':
        csvData = [
          ['Metric', 'Value', 'Change'],
          ['Total Outstanding', formatCurrency(kpiData.totalOutstanding.value), `${kpiData.totalOutstanding.change}%`],
          ['Collections', formatCurrency(kpiData.collections.value), `${kpiData.collections.change}%`],
          ['Overdue Percentage', `${kpiData.overduePercentage.value}%`, `${kpiData.overduePercentage.change}%`],
          ['Avg Collection Period', `${kpiData.avgCollectionPeriod.value} days`, `${kpiData.avgCollectionPeriod.change} days`]
        ];
        filename = 'party-financials-overview';
        break;

      default:
        csvData = [['Export Type', 'Status'], [format, 'Generated']];
        filename = 'financials-export';
    }

    // Convert array to CSV string
    const csvContent = csvData.map(row =>
      row.map(field => `"${field}"`).join(',')
    ).join('\n');

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Export completed - notification removed
  };

  const toggleRowExpansion = (partyId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(partyId)) {
      newExpanded.delete(partyId);
    } else {
      newExpanded.add(partyId);
    }
    setExpandedRows(newExpanded);
  };

  return (
    <div className="space-y-6">
      {/* Header with Filters and Actions */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div>
            <div className="flex items-center space-x-2">
              <h2 className="text-xl font-semibold text-gray-900">Party Financials</h2>
            </div>
            <p className="text-sm text-gray-600">Outstanding, collections, and aging analysis</p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Filters */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="180d">Last 180 days</option>
            <option value="365d">Last 365 days</option>
          </select>

          <select
            value={agingBucket}
            onChange={(e) => setAgingBucket(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="all">All Aging</option>
            <option value="0-30">0-30 days</option>
            <option value="31-60">31-60 days</option>
            <option value="61-90">61-90 days</option>
            <option value="91-120">91-120 days</option>
            <option value="120+">120+ days</option>
          </select>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            <PermissionGate permission="analytics" action="read">
              <button
                onClick={() => handleExport('excel')}
                className="flex items-center space-x-1 px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
              >
                <Download className="h-4 w-4" />
                <span>Export</span>
              </button>
            </PermissionGate>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow"
          onClick={() => handleDrillDown('outstanding')}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Total Outstanding</span>
            <IndianRupee className="h-4 w-4 text-gray-400 font-bold" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-2xl font-bold text-[#0F2942]">{formatCurrency(kpiData.totalOutstanding.value)}</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow"
          onClick={() => handleDrillDown('collections')}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Collections</span>
            <CreditCard className="h-4 w-4 text-gray-400" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-2xl font-bold text-green-600">{formatCurrency(kpiData.collections.value)}</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">% Overdue</span>
            <AlertTriangle className="h-4 w-4 text-gray-400" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-2xl font-bold text-red-600">{kpiData.overduePercentage.value}%</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Avg Collection Period</span>
            <Clock className="h-4 w-4 text-gray-400" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-2xl font-bold text-orange-600">{kpiData.avgCollectionPeriod.value} days</span>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Outstanding by Aging Bucket */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Outstanding by Aging Bucket</h3>
          </div>
          <div className="space-y-4">
            {agingBuckets.map((bucket) => (
              <div key={bucket.bucket} className="hover:bg-gray-50 p-2 rounded"
                onClick={() => handleDrillDown('aging', { bucket: bucket.bucket })}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900">{bucket.bucket}</span>
                  <span className="text-sm font-medium text-gray-900">{formatCurrency(bucket.amount)}</span>
                </div>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                    <div className={`h-2 rounded-full ${bucket.color}`}
                      style={{ width: `${bucket.percentage}%` }}></div>
                  </div>
                  <span className="text-xs text-gray-600">{bucket.percentage}%</span>
                </div>
                <div className="text-xs text-gray-500">{bucket.count} parties</div>
              </div>
            ))}
          </div>
        </div>

        {/* Collections Trend */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Collections Trend</h3>
          </div>
          <div className="space-y-4">
            {collectionsData.map((week) => (
              <div key={week.period} className="border-b border-gray-100 pb-3 last:border-b-0">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{week.period}</span>
                  <span className={`text-sm font-medium ${week.percentage >= 100 ? 'text-green-600' :
                    week.percentage >= 90 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                    {week.percentage}%
                  </span>
                </div>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                    <div className={`h-2 rounded-full ${week.percentage >= 100 ? 'bg-green-500' :
                      week.percentage >= 90 ? 'bg-yellow-500' : 'bg-red-500'
                      }`} style={{ width: `${Math.min(week.percentage, 100)}%` }}></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <span>Target: {formatCurrency(week.target)}</span>
                  <span>Collected: {formatCurrency(week.collected)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Parties Table */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Top Parties by Outstanding</h3>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Party</span>
                    {sortColumn === 'name' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('totalOutstanding')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Outstanding</span>
                    {sortColumn === 'totalOutstanding' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('overdue')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Overdue</span>
                    {sortColumn === 'overdue' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('avgCollectionDays')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Avg Days</span>
                    {sortColumn === 'avgCollectionDays' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('riskScore')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Risk</span>
                    {sortColumn === 'riskScore' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('lastPayment')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Last Payment</span>
                    {sortColumn === 'lastPayment' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {getSortedParties().map((party) => (
                <React.Fragment key={party.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <button
                          onClick={() => toggleRowExpansion(party.id)}
                          className="mr-2 text-gray-400 hover:text-gray-600"
                        >
                          {expandedRows.has(party.id) ?
                            <ChevronDown className="h-4 w-4" /> :
                            <ChevronRight className="h-4 w-4" />
                          }
                        </button>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{party.name}</div>
                          <div className="text-sm text-gray-500">{party.type} • {party.region}</div>
                          <div className="text-xs text-gray-400">RM: {party.rm}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{formatCurrency(party.totalOutstanding)}</div>
                      <div className="text-xs text-gray-500">Limit: {formatCurrency(party.creditLimit)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{formatCurrency(party.overdue)}</div>
                      <div className={`text-xs font-medium ${getOverdueColor(party.overduePercentage)}`}>
                        {party.overduePercentage}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {party.avgCollectionDays} days
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRiskColor(party.riskScore)}`}>
                        {party.riskScore}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(party.lastPayment)}</div>
                      <div className="text-xs text-gray-500">{formatCurrency(party.lastPaymentAmount)}</div>
                    </td>
                  </tr>

                  {/* Expanded Row Content */}
                  {expandedRows.has(party.id) && (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 bg-gray-50">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Aging Breakdown */}
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-3">Aging Breakdown</h4>
                            <div className="space-y-2">
                              {Object.entries(party.agingBreakdown).map(([bucket, amount]) => (
                                <div key={bucket} className="flex justify-between text-xs">
                                  <span className="text-gray-600">{bucket} days:</span>
                                  <span className="font-medium">{formatCurrency(amount)}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Recent Invoices */}
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-3">Recent Invoices</h4>
                            <div className="space-y-2">
                              {party.recentInvoices.map((invoice) => (
                                <div key={invoice.id} className="flex items-center justify-between text-xs">
                                  <div>
                                    <span className="font-medium">{invoice.id}</span>
                                    <span className="text-gray-500 ml-2">{formatDate(invoice.date)}</span>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <span className="font-medium">{formatCurrency(invoice.amount)}</span>
                                    <span className={`inline-flex px-1 py-0.5 text-xs font-semibold rounded ${getStatusColor(invoice.status)}`}>
                                      {invoice.status}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Payment History */}
                          <div className="lg:col-span-2">
                            <h4 className="text-sm font-medium text-gray-900 mb-3">Recent Payments</h4>
                            <div className="space-y-2">
                              {party.paymentHistory.map((payment, index) => (
                                <div key={index} className="flex items-center justify-between text-xs">
                                  <div>
                                    <span className="font-medium">{formatDate(payment.date)}</span>
                                    <span className="text-gray-500 ml-2">{payment.method}</span>
                                    <span className="text-gray-400 ml-2">{payment.reference}</span>
                                  </div>
                                  <span className="font-medium text-green-600">{formatCurrency(payment.amount)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Region Performance */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Regional Performance</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {regionPerformance.map((region) => (
            <div key={region.region} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              onClick={() => handleDrillDown('region', { name: region.region })}>
              <div className="text-center">
                <h4 className="font-medium text-gray-900 mb-2">{region.region}</h4>
                <div className="text-lg font-bold text-[#0F2942] mb-1">
                  {formatCurrency(region.totalOutstanding)}
                </div>
                <div className={`text-sm font-medium mb-2 ${getOverdueColor(region.overduePercentage)}`}>
                  {region.overduePercentage}% overdue
                </div>
                <div className="text-xs text-gray-600 space-y-1">
                  <div>Collections: {formatCurrency(region.collections)}</div>
                  <div>Avg Days: {region.avgCollectionDays}</div>
                  <div>Parties: {region.parties}</div>
                  <div className="text-[#0F2942] font-medium">Top: {region.topParty}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PartyFinancials;
